/**
 * Build a Project — public landing page.
 *
 * The project-side analog of /property-check. Mounted at /build-a-project
 * and reached from the Services hub + the homepage hero. Solves the
 * asymmetry where Property Check had a polished public landing but the
 * Start-a-Project flow dropped visitors straight into /register cold.
 *
 * Mirrors /property-check's structure intentionally so the two service
 * lines read as siblings:
 *   1. Hero + dual CTA (Start / Browse contractors)
 *   2. Trust strip (vetted contractors, verifiers, escrow)
 *   3. How it works — 5 steps from brief to release
 *   4. "Hands-off" row pointing to project managers
 *   5. Footer CTA strip
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Stack,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import HandymanIcon from '@mui/icons-material/Handyman';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../theme/colors';

const STEPS = [
  {
    number: '01',
    title: 'Post your brief',
    body:
      'Describe what you want built or renovated. AI helps you sharpen it into a brief contractors can bid on. About five minutes.',
  },
  {
    number: '02',
    title: 'Get vetted contractor bids',
    body:
      'Multiple bids you can compare side-by-side — profiles, portfolios, ratings, certifications, all verified before they reach you.',
  },
  {
    number: '03',
    title: 'Sign and fund the wallet',
    body:
      'Digitally sign a binding contract. Fund your project wallet — money sits in escrow, not in the contractor\'s account.',
  },
  {
    number: '04',
    title: 'Verifiers document every milestone',
    body:
      'Independent inspectors visit the site, take GPS-tagged timestamped photos, and submit a report before payment can release.',
  },
  {
    number: '05',
    title: 'Approve, and money moves',
    body:
      'You see the evidence in detail and decide. Approve a milestone and the contractor is paid within 72 hours. Reject and nothing moves.',
  },
];

const BuildAProjectLanding = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const startProject = () => navigate(isAuthenticated ? '/dashboard' : '/register');

  return (
    <Box>
      {/* ─── Hero ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: colors.brand.gradient,
          color: '#fff',
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="BUILD WITH CONFIDENCE"
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  mb: 2,
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.5rem' },
                  mb: 2,
                }}
              >
                Build in Zimbabwe, without flying back.
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  opacity: 0.9,
                  fontWeight: 400,
                  mb: 4,
                  maxWidth: 580,
                }}
              >
                Hire vetted contractors, fund work in escrow, and have independent verifiers
                document every milestone before any money moves. Designed for diaspora owners
                running projects 6,000 miles away.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  size="large"
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={startProject}
                  sx={{
                    bgcolor: '#fff',
                    color: colors.brand.primaryDark,
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#f1f5f9' },
                  }}
                >
                  {isAuthenticated ? 'Go to your dashboard' : 'Start a project'}
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  onClick={() => navigate('/contractors')}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: '#fff',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  Browse contractors
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  position: 'relative',
                  aspectRatio: '4/3',
                  backgroundImage: 'url(/images/construction-site.jpg)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── Trust strip ──────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 6 }}
          divider={
            <Box
              sx={{
                display: { xs: 'none', sm: 'block' },
                borderRight: '1px solid',
                borderColor: 'divider',
              }}
            />
          }
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="center"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <HandymanIcon sx={{ color: colors.brand.primary }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Vetted contractors
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Profiles, portfolios, and ratings verified before bidding
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <VerifiedUserIcon sx={{ color: colors.brand.primary }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Independent verifiers
              </Typography>
              <Typography variant="caption" color="text.secondary">
                On-the-ground inspectors confirm every milestone
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <AccountBalanceWalletIcon sx={{ color: colors.brand.primary }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                100% escrow-protected
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Funds release only when you approve the work
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Container>

      {/* ─── How it works ─────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: colors.bg.subtle, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="overline"
            sx={{ color: colors.brand.primary, fontWeight: 700, letterSpacing: '0.1em' }}
          >
            HOW IT WORKS
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              mt: 1,
              mb: 1.5,
              fontSize: { xs: '1.8rem', sm: '2.25rem', md: '2.75rem' },
            }}
          >
            Five steps from brief to release.
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 720, mb: { xs: 4, md: 6 }, fontSize: { xs: '1rem', md: '1.1rem' } }}
          >
            Every project follows the same protected workflow. Money never moves until the work
            is documented and you approve it.
          </Typography>

          <Grid container spacing={{ xs: 2.5, md: 3 }}>
            {STEPS.map((s) => (
              <Grid item xs={12} sm={6} md={4} key={s.number}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2.5,
                    height: '100%',
                    borderColor: colors.border.light,
                    transition: 'border-color 160ms, transform 160ms',
                    '&:hover': {
                      borderColor: colors.brand.primary,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.brand.primary,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        fontSize: '0.8rem',
                      }}
                    >
                      STEP {s.number}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mt: 0.5, mb: 1, lineHeight: 1.25 }}
                    >
                      {s.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {s.body}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Hands-off / project-managers row removed — feature ships in v2.
          Restore from git history (BuildAProjectLanding.js pre-v2 hide) when
          project manager hiring goes live. */}

      {/* ─── Footer CTA strip ─────────────────────────────────────────── */}
      <Box
        sx={{
          background: colors.brand.gradient,
          color: '#fff',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              mb: 2,
              fontSize: { xs: '1.8rem', sm: '2.25rem', md: '2.5rem' },
            }}
          >
            Ready to build?
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: 400, opacity: 0.9, mb: 4, fontSize: { xs: '1rem', md: '1.1rem' } }}
          >
            Sign up and post your first brief in five minutes. No commitment until you've seen
            the bids.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              size="large"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={startProject}
              sx={{
                bgcolor: '#fff',
                color: colors.brand.primaryDark,
                fontWeight: 700,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                '&:hover': { bgcolor: '#f1f5f9' },
              }}
            >
              {isAuthenticated ? 'Go to your dashboard' : 'Start a project'}
            </Button>
            <Button
              size="large"
              variant="outlined"
              onClick={() => navigate('/contractors')}
              sx={{
                borderColor: 'rgba(255,255,255,0.5)',
                color: '#fff',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Browse contractors
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default BuildAProjectLanding;
